import { InvitationAttendeeState, reportingPrisma, type Prisma } from "client";

export type InvitationAttendeeStatusCounts = {
  confirmed: number;
  rejected: number;
  maybe: number;
  pendingInvitee: number;
  notResponded: number;
  total: number;
};

const NOT_RESPONDED_STATES: InvitationAttendeeState[] = [
  InvitationAttendeeState.CREATED,
  InvitationAttendeeState.SENT,
  InvitationAttendeeState.REMINDER,
];

const EMPTY_INVITATION_ATTENDEE_STATUS_COUNTS: InvitationAttendeeStatusCounts =
  {
    confirmed: 0,
    rejected: 0,
    maybe: 0,
    pendingInvitee: 0,
    notResponded: 0,
    total: 0,
  };

type StateCountRow = { _id?: string; count?: number };

function countsFromStateRows(
  rows: StateCountRow[]
): InvitationAttendeeStatusCounts {
  const byState = Object.fromEntries(
    rows.map((row) => [row._id, Number(row.count ?? 0)])
  ) as Partial<Record<InvitationAttendeeState, number>>;

  const count = (...states: InvitationAttendeeState[]) =>
    states.reduce((sum, state) => sum + (byState[state] ?? 0), 0);

  return {
    confirmed: count(InvitationAttendeeState.CONFIRMED),
    rejected: count(InvitationAttendeeState.DECLINED),
    maybe: count(InvitationAttendeeState.MAYBE),
    pendingInvitee: count(InvitationAttendeeState.PENDING_INVITEE),
    notResponded: count(...NOT_RESPONDED_STATES),
    total: rows.reduce((sum, row) => sum + Number(row.count ?? 0), 0),
  };
}

/**
 * Single aggregation: Invitation.eventId → attendees → groupBy state.
 * Avoids loading all invitation ids into an $in list.
 */
export const getInvitationAttendeeStatusCountsByEventId = async (
  eventId: string
): Promise<InvitationAttendeeStatusCounts> => {
  const raw = await reportingPrisma.invitation.aggregateRaw({
    pipeline: [
      { $match: { eventId: { $oid: eventId } } },
      { $project: { _id: 1 } },
      {
        $lookup: {
          from: "InvitationAttendee",
          localField: "_id",
          foreignField: "invitationId",
          pipeline: [{ $project: { state: 1 } }],
          as: "attendees",
        },
      },
      { $unwind: "$attendees" },
      {
        $group: {
          _id: "$attendees.state",
          count: { $sum: 1 },
        },
      },
    ],
  });

  const rows = (Array.isArray(raw) ? raw : []) as StateCountRow[];
  if (rows.length === 0) {
    return EMPTY_INVITATION_ATTENDEE_STATUS_COUNTS;
  }
  return countsFromStateRows(rows);
};

export const getInvitationAttendeeStatusCounts = async (
  whereClause: Prisma.InvitationAttendeeWhereInput
): Promise<InvitationAttendeeStatusCounts> => {
  const rows = await reportingPrisma.invitationAttendee.groupBy({
    by: ["state"],
    where: whereClause,
    _count: true,
  });

  return countsFromStateRows(
    rows.map((row) => ({ _id: row.state, count: row._count }))
  );
};
